// 下載中心彈窗（移植自 crm/src/pages/tasks/ExportCenterDialog.jsx，PRD §5.12/§5.13）。列出本人的匯出／報告工作：
//   處理中／重試中 → spinner + 取消；成功 → 下載 icon；失敗／已取消 → 狀態文字。開啟時清紅點並重抓清單。
import { useEffect } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Dialog } from '@/components/ui/Dialog'
import { OverlayScroll } from '@/components/ui/OverlayScroll'
import { DownloadIcon } from '@/components/ui/icons'
import { addToast } from '@/components/ui/Toast'
import { getAttachmentUrl } from '@/utils'
import { t } from '@/i18n'
import { getExportOrders, cancelExportOrder, recordExportDownload } from '../api/exportOrdersApi'
import { EXPORT_ORDERS_KEY, STATUS, clearExportNotification, updateOrderInCache } from '../exportCenter'

// action_type → [i18n scope, key]：t() 不跨 scope fallback，線路／集團匯出標題在 line／merchant scope，所以連 scope 一起帶。
// ⚠️ 目前全部命中不了：後端寫進 export_order.action_type 的一律是佇列名 crm_export，真正的型別只在 job payload；
// 留著是因為後端改成存型別後就會生效，修徽章屬另一張單（詳見舊版註解）。新增匯出型別時要同步這張表。
const ACTION_LABEL = {
  task_export_zip: ['app', 'Export Data'],
  // task_export_csv 是 v3.2 的舊產出，改版當下佇列裡可能還有，兩者都要留
  task_export_csv: ['app', 'Export Data'],
  task_export_zip_rows: ['app', 'Export Data'],
  task_report_csv: ['app', 'Task Report'],
  lines_export: ['line', 'Export Line Data'],
  merchant_export: ['merchant', 'Export Merchant Data'],
  campaign_export: ['campaign', 'Export Campaign Data'],
  campaign_export_with_pids: ['campaign', 'Export Campaign PIDs'],
  // PSD-494
  line_file_export: ['line', 'Documents'],
  merchant_file_export: ['merchant', 'Contracts'],
  monthly_bet_template_export: ['line', 'Monthly Bet Amount Template'],
}
const STATUS_LABEL = { 1: 'Processing', 2: 'Retrying', 3: 'Failed', 4: 'Success', 5: 'Canceled' }

const inProgress = (o) => !o.finished && (o.status === STATUS.PROCESSING || o.status === STATUS.RETRYING)
const isSuccess = (o) => o.finished && o.status === STATUS.SUCCESS && !!o.filepath

function OrderStatus({ order, onCancel }) {
  // 下載紀錄只是稽核：不等回應、失敗不出聲（不擋瀏覽器下載）
  const recordDownload = useMutation({ mutationFn: recordExportDownload, meta: { silentError: true } })
  if (inProgress(order)) {
    return (
      <>
        <span className="loading loading-spinner loading-xs text-brand-black-400" />
        <button type="button" className="cursor-pointer text-xs text-brand-black-600 hover:text-brand-black-1000" onClick={() => onCancel(order.order_no)}>
          {t('app', 'Cancel')}
        </button>
      </>
    )
  }
  if (isSuccess(order)) {
    return (
      <a
        className="text-brand-black-400 transition-colors hover:text-brand-primary"
        href={getAttachmentUrl(order.filepath, order.file_name)}
        download={order.file_name}
        title={order.file_name}
        onClick={() => recordDownload.mutate(order.order_no)}
      >
        <DownloadIcon className="size-4" />
      </a>
    )
  }
  return (
    <span className={`text-xs font-medium ${order.status === STATUS.FAILED ? 'text-error' : 'text-brand-black-400'}`}>{t('app', STATUS_LABEL[order.status] ?? 'Unknown')}</span>
  )
}

export function ExportCenterDialog({ open, onClose }) {
  // 每次打開都重抓（staleTime 0），關著的時候不抓；清單整個 session 保留（同舊版模組 store），重開先顯示上一份不閃空
  const { data } = useQuery({
    queryKey: EXPORT_ORDERS_KEY,
    queryFn: ({ signal }) => getExportOrders({ signal }),
    enabled: open,
    staleTime: 0,
    gcTime: Infinity,
  })
  const orders = data?.data ?? []

  useEffect(() => {
    if (open) clearExportNotification()
  }, [open])

  const cancel = useMutation({
    mutationFn: cancelExportOrder,
    meta: { silentError: true },
    onSuccess: (_res, orderNo) => updateOrderInCache(orderNo, { finished: true, status: STATUS.CANCELED }),
    onError: () => addToast({ level: 'error', message: t('app', 'Cancel Failed') }),
  })

  return (
    <Dialog open={open} onClose={onClose} title={t('app', 'Download Tasks')} className="max-w-[520px]" bodyClassName="px-4 py-4" overflowVisible>
      {/* PSD-401 對 Figma 7158:56383：body 四邊 16px；限高 302px（≈5 列）超過即捲動。
          thumb 對 Figma 10423:88172（right 4px／w 5px），用負 right 推進 body 內距（桌面 −12、手機 −8） */}
      <OverlayScroll viewportClassName="max-h-[302px]" className="flex flex-col gap-2" thumbClassName="-right-3 w-[5px] max-md:-right-2">
        {orders.length > 0 ? (
          orders.map((o) => (
            <div key={o.order_no} className="flex flex-col gap-0.5 overflow-hidden rounded-brand-base border border-brand-black-50 bg-base-100 p-2">
              <div className="flex items-center justify-between gap-2 px-1">
                <span className="truncate text-xs font-medium leading-[18px] text-brand-black-900" title={o.file_name}>
                  {o.file_name || t('app', 'Pending Filename')}
                </span>
                <div className="flex shrink-0 items-center gap-2">
                  <OrderStatus order={o} onCancel={(orderNo) => cancel.mutate(orderNo)} />
                </div>
              </div>
              <div className="px-1">
                <span className="inline-flex items-center rounded-full bg-brand-black-50 px-1.5 text-[10px] leading-[18px] text-brand-black-600">
                  {t(...(ACTION_LABEL[o.action_type] ?? ['app', o.action_type]))}
                </span>
              </div>
            </div>
          ))
        ) : (
          <p className="py-10 text-center text-sm text-brand-black-400">{t('app', 'No Export Records')}</p>
        )}
      </OverlayScroll>
    </Dialog>
  )
}
